import React from 'react';
import { useCreatePath, TabbedForm, Create, useRedirect, FormGroupContextProvider } from 'react-admin';
import { Container, Typography } from '@mui/material';
import { palette, Theme } from 'src/Theme';
import { editContainerStyles } from 'src/styles';
import { ReactComponent as ChevronRight } from 'src/assets/chevron-right.svg';
import { useClearAutoSaveOnUnmount } from 'src/hooks/useClearAutoSaveOnUnmount';
import { getAutoSaveKey, AUTOSAVE_RESOURCES } from 'src/constants/autoSave';
import { ShsReturnButton, AutoSaveFormListener, useClearAutoSave } from 'src/components/common';
import { GeneralCenterInfo, CenterDepartment, CenterLessonsPlace } from 'src/components/EducationalCenters/FieldGroups';
import { centerDefaultValues } from 'src/constants/centers';
import { TabLabel } from '../Employees/components/TabLabel';
import tabGroups from './const/tab-groups';
import useTabbedFormNavigation from '../common/ShsTabbedFormToolbar/useTabIndex';
import { ShsTabbedFormToolbar } from '../common/ShsTabbedFormToolbar';
import { HospitalWorkingInfo } from './FieldGroups/HospitalWorkingInfo';

const tabbedFormStyles = {
    'a.form-tab.MuiTab-labelIcon': {
        flex: '0 1 auto'
    },
    '.MuiTabs-indicator': {
        display: 'none'
    },
    '.Mui-selected.Mui-disabled': {
        color: Theme.palette.primary.main
    }
};

const tabStyles = {
    padding: 0,
    '&.MuiTab-labelIcon': {
        flexFlow: 'row-reverse'
    },
    '.MuiTab-iconWrapper': {
        margin: '0 6px'
    }
};

export const EducationalCenterAdd: React.FC = () => {
    const redirect = useRedirect();
    const { tabIndex } = useTabbedFormNavigation();
    const createPath = useCreatePath();
    const storageKey = getAutoSaveKey(AUTOSAVE_RESOURCES.CENTER);
    const { clearAutoSave } = useClearAutoSave(storageKey);
    const { markAsSubmitting } = useClearAutoSaveOnUnmount(storageKey);

    const navigateToCentersList = () => redirect(createPath({ resource: 'centers', type: 'list' }));

    const onSuccess = () => {
        markAsSubmitting();
        clearAutoSave();
        navigateToCentersList();
    };

    const handleBackClick = () => {
        clearAutoSave();
        navigateToCentersList();
    };

    return (
        <Container sx={{ ...editContainerStyles, maxWidth: '776px' }} maxWidth={false}>
            <ShsReturnButton onClick={handleBackClick} label="Назад до освітніх центрів" />
            <Create mutationOptions={{ onSuccess }}>
                <Typography variant="h4" component="h3" mb={4} fontWeight="700">
                    Додати освітній центр
                </Typography>
                <TabbedForm
                    sx={tabbedFormStyles}
                    mode="onBlur"
                    toolbar={<ShsTabbedFormToolbar tabGroups={tabGroups} saveTitle="Додати центр" />}
                    defaultValues={centerDefaultValues}>
                    <TabbedForm.Tab
                        label={<TabLabel label="Загальна інформація" index={0} />}
                        icon={<ChevronRight stroke={palette.primary.gray} />}
                        sx={tabStyles}
                        disabled={tabIndex === 0}>
                        <AutoSaveFormListener storageKey={storageKey} />
                        <FormGroupContextProvider name={tabGroups[0]}>
                            <GeneralCenterInfo />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    <TabbedForm.Tab
                        label={<TabLabel label="Лікарня" index={1} />}
                        icon={<ChevronRight stroke={palette.primary.gray} />}
                        sx={tabStyles}
                        disabled={tabIndex < 1}>
                        <FormGroupContextProvider name={tabGroups[1]}>
                            <HospitalWorkingInfo />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    <TabbedForm.Tab
                        label={<TabLabel label="Відділення" index={2} />}
                        icon={<ChevronRight stroke={palette.primary.gray} />}
                        sx={tabStyles}
                        disabled={tabIndex < 2}>
                        <FormGroupContextProvider name={tabGroups[2]}>
                            <CenterDepartment />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                    <TabbedForm.Tab
                        label={<TabLabel label="Місця проведення уроків" index={3} />}
                        sx={tabStyles}
                        disabled={tabIndex < 3}>
                        <FormGroupContextProvider name={tabGroups[3]}>
                            <CenterLessonsPlace />
                        </FormGroupContextProvider>
                    </TabbedForm.Tab>
                </TabbedForm>
            </Create>
        </Container>
    );
};
